import React from "react"
import {useTranslation} from "react-i18next"

// Components
import Icon from "../../../../../components/modern/Icon"
import MiniEcg from "../../../../../components/modern/MiniEcg"
import ModernTooltip from "../../../../../components/modern/Tooltip"

// Models
import {AvailableRecordType} from "../../../models"

// Utils
import {formatRecordTime, hasResult, recordAge, resultMeta} from "../ModernRecordsCard/columns"

// Styles
import useStyles from "./styles"

type ModernInProgressBannerProps = {
    record: AvailableRecordType | null
    fetching: boolean
    onContinue(): void
}

/**
 * "Review in progress" banner.
 *
 * Reproduces Classic's InProgressSection guards exactly: nothing renders while
 * the request is in flight, when there is no in-progress record, or when the
 * record is not an ECG — the record page cannot resume a HOLTER, so offering
 * the CTA would dead-end the user.
 *
 *
 * @param props
 */
export default function ModernInProgressBanner(props: ModernInProgressBannerProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {record, fetching, onContinue} = props;

    if (fetching || record === null) {
        return null
    }

    if (record.recordType !== "ECG") {
        return null
    }

    const result = resultMeta(record);
    const resultClass = result.severity === "ok" ? styles.res__ok : styles.res__warn;
    const age = recordAge(record);

    return (
      <section className={styles.banner} aria-label={t("modern.review_in_progress")}>
          <span className={styles.pulseicon}>
              <Icon name="pulse"/>
          </span>

          <div className={styles.info}>
              <span className={styles.microlabel}>{t("modern.review_in_progress")}</span>
              <span className={styles.fname} title={record.fileName}>
                  {record.fileName}
                  {age !== null && (
                    <span className={styles.age}>{t("modern.banner_age", {age})}</span>
                  )}
              </span>
              <span className={styles.since}>
                  <span>{`${t("modern.opened")} ${formatRecordTime(record)}`}</span>
                  {hasResult(result) && (
                    <ModernTooltip text={result.title}>
                        <span className={`${styles.res} ${resultClass}`}>
                            <i className={styles.dot}/>
                            <span className={styles.res_text}>
                                {result.label === null ? t(result.textKey as string) : result.label}
                            </span>
                        </span>
                    </ModernTooltip>
                  )}
              </span>
          </div>

          <MiniEcg className={styles.miniecg}/>

          <button type="button" className={styles.cta} onClick={onContinue}>
              {t("modern.continue_review")}
              <Icon name="arrow"/>
          </button>
      </section>
    )
}
